import { EmberMinimapState } from "game-common/models"

// What the camp still owes the player's attention, derived from the minimap feed.
//
// Kept free of PIXI (like roster_speech_lane.ts and important_lane.ts) so the rules can be
// unit-tested without a renderer; ember_obligations_panel.ts owns the drawing.
//
// WHY THIS EXISTS. Every expedition beat reached the player as an EVENT -- a line on the `important`
// strip, said once, at the noisiest moment the camp has (the wave it has just survived). The
// player's question a minute later is not "what happened" but "what is still live, and what is it
// costing me". A stage-2 ritual with forty seconds to wave 8 is a STANDING OBLIGATION, and the only
// standing representation of it was a dot inside a panel that collapses and remembers that it is
// collapsed (`_emberMinimapCollapsed_`), plus a chant that is silent for anyone playing muted.
//
// NO NEW SERVER WORK. `EmberMinimapState` already carries everything below and already arrives once
// a second (MINIMAP_INTERVAL_MS): `rescue.state`, and `bonecaller.state/stage/warned`. This is a
// second reader of a feed that was already on the wire.

export type ObligationTone = "steady" | "alert"

export interface Obligation {
    /** Stable across state changes, so a row keeps its place in the stack as its detail moves. */
    key: string
    icon: string
    label: string
    detail: string
    tone: ObligationTone
    /**
     * What makes this a DIFFERENT state from the one the player dismissed.
     *
     * Dismissal is "I have read this", not "never tell me again": a row the player waved away comes
     * back the moment what it says changes -- the ritual advancing a stone, the survivor coming
     * under attack. Nothing the player chose to hide can therefore hide a change from them.
     */
    signature: string
}

/**
 * The ritual always rises on wave 8 (BonecallerLifecycle.waveLanded), which is why the deadline can
 * be stated rather than counted. The minimap label says the same thing in the same words.
 */
const RITUAL_WAVE = 8

export const deriveObligations = (state?: EmberMinimapState | null): Obligation[] => {
    if (!state) return []
    const obligations: Obligation[] = []
    const rescue = state.rescue
    if (rescue) {
        // Only the three live states exist on the wire: the marker itself is dropped once she is
        // admitted (the swap to ember-recruit.js replaces flags wholesale, so `isSurvivor` goes with
        // it) or once she is dead. So there is no terminal state to filter out here.
        const danger = rescue.state === "In danger"
        obligations.push({
            key: "rescue",
            icon: "🆘",
            label: "Survivor at the wreck",
            detail:
                rescue.state === "Following"
                    ? "escorting her to the fire"
                    : danger
                      ? "under attack — drive them off"
                      : "waiting for a hand up",
            tone: danger ? "alert" : "steady",
            signature: `rescue:${rescue.state}`,
        })
    }
    const ritual = state.bonecaller
    if (ritual && ritual.state === "active") {
        obligations.push({
            key: "bonecaller",
            icon: "🩸",
            label: "Bonecaller",
            detail: `stage ${ritual.stage} of 3 · rises on wave ${RITUAL_WAVE}`,
            // `warned` is set when the wave-8 countdown starts, which is the last moment the player
            // can still leave and stop it. That is the one point where this stops being a standing
            // fact and becomes something to act on now.
            tone: ritual.warned ? "alert" : "steady",
            signature: `ritual:${ritual.stage}:${ritual.warned}`,
        })
    }
    if (state.mine && !state.mine.explored) {
        obligations.push({ key: "mine", icon: "⛏", label: "Explore mine",
            detail: "a recruit found a cave · marked on your map", tone: "steady", signature: "mine:unexplored" })
    }
    return obligations
}

/**
 * Which obligations the player has waved away, and until when.
 *
 * Keyed by obligation, holding the SIGNATURE that was dismissed -- so the row returns on a change
 * (see Obligation.signature) and nothing is ever hidden permanently by one tap.
 */
export class ObligationDismissals {
    private dismissed = new Map<string, string>()

    dismiss = (obligation: Obligation) => this.dismissed.set(obligation.key, obligation.signature)

    visible = (obligations: Obligation[]): Obligation[] =>
        obligations.filter(o => this.dismissed.get(o.key) !== o.signature)

    /** A new run, or a new room: nothing carried over has anything to say about this camp. */
    reset = () => this.dismissed.clear()
}
